/**
 * Carga inicial do catálogo (F6-T11).
 *
 * O catálogo é servido como arquivo estático junto do app, não embutido no bundle:
 * são ~300 KB que mudam num ritmo próprio, e embuti-los forçaria um novo deploy do
 * shell a cada atualização do glossário.
 */
import { applyCatalog, getCatalogMetadata } from '@/db/repositories/catalog';
import { catalogSchema, SUPPORTED_SCHEMA_VERSION, type Catalog } from '@/types/catalog';

/** Base de onde vêm `catalog.json` e `manifest.json`. */
export const CATALOG_BASE_URL: string = (
  import.meta.env.VITE_CATALOG_BASE_URL ?? '/catalog'
).replace(/\/$/, '');

export class CatalogValidationError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'CatalogValidationError';
  }
}

/**
 * Busca e valida um catálogo remoto.
 *
 * A validação acontece **antes** de qualquer escrita no banco: um catálogo inválido
 * não pode corromper a cópia local que já funciona (PLAN §13, critérios de aceite).
 */
export async function fetchCatalog(signal?: AbortSignal): Promise<Catalog> {
  const url = `${CATALOG_BASE_URL}/catalog.json`;

  let payload: unknown;
  try {
    const response = await fetch(url, {
      cache: 'no-cache',
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) {
      throw new CatalogValidationError(`Catálogo respondeu HTTP ${response.status}`);
    }
    payload = await response.json();
  } catch (error) {
    if (error instanceof CatalogValidationError) throw error;
    throw new CatalogValidationError('Não foi possível baixar o catálogo', { cause: error });
  }

  const parsed = catalogSchema.safeParse(payload);
  if (!parsed.success) {
    throw new CatalogValidationError(
      `Catálogo remoto não passou na validação de schema (${parsed.error.issues.length} problemas)`,
      { cause: parsed.error },
    );
  }

  if (parsed.data.schemaVersion > SUPPORTED_SCHEMA_VERSION) {
    throw new CatalogValidationError(
      `Catálogo usa schemaVersion ${parsed.data.schemaVersion}, mas esta versão do app ` +
        `entende no máximo ${SUPPORTED_SCHEMA_VERSION}. Atualize a aplicação.`,
    );
  }

  return parsed.data;
}

export interface SeedResult {
  seeded: boolean;
  reason: 'already-present' | 'applied' | 'failed';
  catalogVersion?: string;
  error?: unknown;
}

/**
 * Garante que exista um catálogo local.
 *
 * Não faz nada se já houver um: a atualização é responsabilidade do serviço de
 * sincronização, que sabe comparar versões. Aqui só cobrimos a primeira execução.
 * Falha de rede não é erro — o app simplesmente ainda não tem catálogo, e a UI
 * mostra o estado vazio correspondente.
 */
export async function seedCatalogIfEmpty(): Promise<SeedResult> {
  const existing = await getCatalogMetadata();
  if (existing) {
    return { seeded: false, reason: 'already-present', catalogVersion: existing.catalogVersion };
  }

  try {
    const catalog = await fetchCatalog();
    await applyCatalog(catalog);
    return { seeded: true, reason: 'applied', catalogVersion: catalog.catalogVersion };
  } catch (error) {
    console.warn('[db] não foi possível carregar o catálogo inicial', error);
    return { seeded: false, reason: 'failed', error };
  }
}
